import { useState } from 'react';
import {
  Button,
  Alert,
  AlertDescription,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  useAuth,
} from '@aaas/shared';
import type { SlackIntegration } from '@aaas/shared';
import { MessageSquare, CheckCircle2, Loader2, Shield } from 'lucide-react';
import { useWorkspaceSlackOAuthUrl } from '../hooks/useIntegrationQuery';
import { openSlackOAuthPopup } from '../utils/slackOAuth';

interface SlackIntegrationCardProps {
  integration?: SlackIntegration | null;
}

const SlackIntegrationCard: React.FC<SlackIntegrationCardProps> = ({ integration }) => {
  const { idToken, currentWorkspace } = useAuth();
  const [showPermissionsDialog, setShowPermissionsDialog] = useState(false);
  const [oauthError, setOauthError] = useState<string | null>(null);

  const workspaceOAuthUrlMutation = useWorkspaceSlackOAuthUrl();

  const handleConnect = async () => {
    if (!idToken || !currentWorkspace?.id) {
      setOauthError('Authentication required');
      return;
    }

    setOauthError(null);

    try {
      // Get OAuth URL from backend (workspace-level only)
      const response = await workspaceOAuthUrlMutation.mutateAsync({
        idToken,
        workspaceId: currentWorkspace.id,
      });

      // Open OAuth popup
      openSlackOAuthPopup(response.oauth_url, (result) => {
        if (result.success) {
          // OAuth completed successfully
          // The page will auto-reload or the query will refetch
          window.location.reload();
        } else if (result.error) {
          setOauthError(result.error);
        }
      });
    } catch (error: unknown) {
      const errorMessage =
        error && typeof error === 'object' && 'message' in error
          ? (error as { message: string }).message
          : 'Failed to initiate OAuth flow';
      setOauthError(errorMessage);
    }
  };

  const isLoading = workspaceOAuthUrlMutation.isPending;

  if (!integration) {
    // Not connected - show connect button
    return (
      <div className="bg-white rounded-lg border p-6 space-y-4">
        <div className="flex items-start gap-4">
          <div className="p-3 bg-purple-100 rounded-lg">
            <MessageSquare className="w-6 h-6 text-purple-600" />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold mb-1">Slack Integration</h3>
            <p className="text-sm text-gray-600 mb-4">
              Connect your Slack workspace to enable team members to ask questions via direct
              messages to the bot. This will create a <strong>workspace-level</strong> integration
              that works for all processes in this workspace.
            </p>

            {oauthError && (
              <Alert variant="destructive" className="mb-4">
                <AlertDescription>{oauthError}</AlertDescription>
              </Alert>
            )}

            {workspaceOAuthUrlMutation.isPending ? (
              <Button disabled className="bg-purple-600">
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Connecting...
              </Button>
            ) : (
              <button
                onClick={handleConnect}
                disabled={isLoading}
                className="cursor-pointer transition-opacity hover:opacity-80 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <img
                  alt="Add to Slack"
                  height="40"
                  width="139"
                  src="https://platform.slack-edge.com/img/add_to_slack.png"
                  srcSet="https://platform.slack-edge.com/img/add_to_slack.png 1x, https://platform.slack-edge.com/img/add_to_slack@2x.png 2x"
                />
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Connected - show integration details
  return (
    <div className="bg-white rounded-lg border p-6 space-y-4">
      <div className="flex items-start justify-between">
        <div className="flex items-start gap-4">
          <div className="p-3 bg-green-100 rounded-lg">
            <CheckCircle2 className="w-6 h-6 text-green-600" />
          </div>
          <div>
            <h3 className="text-lg font-semibold mb-1">Slack Integration</h3>
            <p className="text-sm text-gray-600">Connected to your Slack workspace</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="px-3 py-1 bg-green-100 text-green-700 text-xs font-medium rounded-full">
            Active
          </span>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
        <div>
          <p className="text-xs text-gray-500 mb-1">Integration Level</p>
          <p className="text-sm font-medium">
            <span className="inline-flex items-center gap-1">
              <span className="w-2 h-2 rounded-full bg-blue-500"></span>
              Workspace-level (all processes)
            </span>
          </p>
        </div>
        <div>
          <p className="text-xs text-gray-500 mb-1">Workspace</p>
          <p className="text-sm font-medium">{integration.team_name}</p>
        </div>
        <div>
          <p className="text-xs text-gray-500 mb-1">Connected By</p>
          <p className="text-sm font-medium">{integration.admin_email || 'Unknown'}</p>
        </div>
        <div>
          <p className="text-xs text-gray-500 mb-1">Connected At</p>
          <p className="text-sm font-medium">
            {new Date(integration.connected_at).toLocaleDateString()}
          </p>
        </div>
        <div>
          <p className="text-xs text-gray-500 mb-1">Bot Scopes</p>
          <button
            onClick={() => setShowPermissionsDialog(true)}
            className="text-sm font-medium text-blue-600 hover:text-blue-800 hover:underline cursor-pointer flex items-center gap-1"
          >
            <Shield className="w-3.5 h-3.5" />
            {integration.scopes.split(',').length} permissions
          </button>
        </div>
      </div>

      {/* Permissions Dialog */}
      <Dialog open={showPermissionsDialog} onOpenChange={setShowPermissionsDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Shield className="w-5 h-5" />
              Bot Permissions & Scopes
            </DialogTitle>
            <DialogDescription>
              These are the permissions granted to the Slack bot for workspace "
              {integration.team_name}"
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid gap-2">
              {integration.scopes.split(',').map((scope, index) => (
                <div
                  key={index}
                  className="flex items-start gap-3 p-3 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors"
                >
                  <div className="flex-shrink-0 mt-0.5">
                    <div className="w-2 h-2 rounded-full bg-green-500"></div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <code className="text-sm font-mono text-gray-900 break-all">
                      {scope.trim()}
                    </code>
                    <p className="text-xs text-gray-600 mt-1">
                      {getScopeDescription(scope.trim())}
                    </p>
                  </div>
                </div>
              ))}
            </div>
            <div className="pt-4 border-t">
              <p className="text-xs text-gray-500">
                Total: {integration.scopes.split(',').length} permissions granted
              </p>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Helper function to provide descriptions for common Slack scopes
function getScopeDescription(scope: string): string {
  const descriptions: Record<string, string> = {
    'app_mentions:read': 'View messages that directly mention the bot',
    'channels:history': 'View messages and content in public channels',
    'channels:read': 'View basic information about public channels',
    'chat:write': 'Send messages as the bot',
    commands: 'Add shortcuts and/or slash commands',
    'files:read': 'View files shared in channels and conversations',
    'groups:history': 'View messages and content in private channels',
    'groups:read': 'View basic information about private channels',
    'im:history': 'View messages and content in direct messages',
    'im:read': 'View basic information about direct messages',
    'im:write': 'Start direct messages with people',
    'mpim:history': 'View messages and content in group direct messages',
    'mpim:read': 'View basic information about group direct messages',
    'reactions:read': 'View emoji reactions and their associated content',
    'team:read': 'View basic information about workspace',
    'users:read': 'View people in the workspace',
    'users:read.email': 'View email addresses of people in the workspace',
  };

  return descriptions[scope] || 'Slack bot permission';
}

export default SlackIntegrationCard;
